import { observer } from 'mobx-react';
import { Paper } from '@mui/material';
import { NetworkStatus } from '@apollo/client';
import { useParams, useRouteLoaderData } from 'react-router-dom';

import {
  CampContactInput,
  CampContactsDocument,
  CampContactUpdate,
  CampQuery,
  useCampContactsQuery,
  useCampPermissionQuery,
  useCreateCampContactMutation,
  useDeleteCampContactMutation,
  useUpdateCampContactMutation,
} from '../../../../../../generated/graphql';
import {
  CacheOperation,
  updateCacheWithCampId,
} from '../../../../../../utils/updateCache';
import { CustomDialog } from '../../../../../../components/common/CustomDialog';
import { ErrorLoading } from '../../../../../../components/common/ErrorLoading';
import { AutoTableModal } from '../../../../../../components/Modals/AutoTableModal';
import {
  TableComponent,
  TableHeader,
} from '../../../../../../components/Table/Table';
import { TableColumn } from '../../../../../../types/tableColumn';
import { useStores } from '../../../../../../stores';
import { InputType } from '../../../../../../types/inputTypes';
import React, { useEffect, useState } from 'react';

const headers: TableColumn[] = [
  {
    title: 'Namn',
    accessor: 'name',
    inputType: InputType.Text,
  },
  {
    title: 'Beskrivning',
    accessor: 'description',
    inputType: InputType.Text,
  },
  {
    title: 'Telefon',
    accessor: 'phone',
    inputType: InputType.Text,
  },
  {
    title: 'Epost',
    accessor: 'email',
    inputType: InputType.Text,
  },
  {
    title: 'Behörigheter',
    accessor: 'permissions',
    inputType: InputType.Permissions,
  },
];

export const AppContactPage = observer(function AppContactPage() {
  const stores = useStores();
  const modalStore = stores.modalStore;
  const camp = useRouteLoaderData('camp') as CampQuery['camp'];
  const camp_id = camp.camp_id;
  const params = useParams();
  const card_id = parseInt(params.card_id);
  const [modalHeaders, setModalHeaders] = React.useState(headers);
  const { data, error, loading, refetch, networkStatus } = useCampContactsQuery(
    { variables: { card_id: card_id } },
  );

  const [permissions, setPermissions] = useState([]);

  const permissionsQuery = useCampPermissionQuery({ variables: { camp_id } });

  useEffect(() => {

    if (!permissionsQuery.data) return;

    const permissionData = permissionsQuery.data.campPermissions;

    const permissionHeaders = permissionData.map((p) => ({
      title: p.name,
      accessor: `permission_${p.id}`,
      inputType: InputType.Boolean,
    }));

    setModalHeaders([...headers, ...permissionHeaders]);

    if (permissionsQuery.data)
      {
        setPermissions(permissionsQuery.data.campPermissions)
      }

  }, [permissionsQuery.data]);

  const [deleteItem] = useDeleteCampContactMutation();

  const refetching = networkStatus === NetworkStatus.refetch;

  const [updateItem] = useUpdateCampContactMutation();

  if (!data) {
    return <ErrorLoading {...{ loading, error }} />;
  }

  return (
    <>
      <Paper>
        <TableHeader
          title={'Kontaktinfo'}
          onAddItem={() => {
            modalStore.addItem();
          }}
        />
        {!!data.campContacts && (
          <TableComponent
            headers={headers}
            rows={data.campContacts}
            showEdit
            permissions={permissions}
            onUpdateRow={(id, accessor, newValue) => {
              return updateItem({
                variables: {
                  campContact: {
                    card_id,
                    id: id as string,
                    [accessor]: newValue,
                  },
                },
              });
            }}
            onEdit={(row) => modalStore.updateItem(row)}
            onDelete={(row) => {
              deleteItem({
                variables: {
                  card_id,
                  campContactId: String(row.id),
                },
                update: (proxy: any) =>
                  updateCacheWithCampId({
                    query: CampContactsDocument,
                    proxy,
                    method: CacheOperation.DELETE,
                    card_id: card_id!,
                    mutationResultObject: null,
                    id: row.id,
                  }),
              });
            }}
          />
        )}

        <AddItem camp_id={camp_id!} card_id={card_id} headers={modalHeaders} />
        <EditItem camp_id={camp_id!} headers={modalHeaders} card_id={card_id} />
      </Paper>
    </>
  );
});

const AddItem = observer(
  ({ camp_id, headers, card_id }: { camp_id: number; headers: TableColumn[], card_id: number }) => {
    const stores = useStores();
    const modalStore = stores.modalStore;

    const [createItem] = useCreateCampContactMutation();

    const close = modalStore.closeAddItemModal;

    return (
      <CustomDialog open={modalStore.showingAddItemModal} onClose={close}>
        <AutoTableModal
          title="kontakt"
          headers={headers}
          onCancel={close}
          onSubmit={async (data) => {
            data.card_id = card_id;

            let permissionData = [];

            for (const [key, value] of Object.entries(data)) {
              if (key.includes('permission_')) {
                permissionData.push({
                  id: parseInt(key.split('_')[1]),
                  value: value
                });

                delete data[key];
              }
            }

            data.permissions = JSON.stringify(permissionData);

            await createItem({
              variables: {
                campContact: data as CampContactInput,
              },
              update: (
                proxy: any,
                mutationResult: { data: { createCampContact: any } },
              ) => {
                updateCacheWithCampId({
                  query: CampContactsDocument,
                  proxy,
                  method: CacheOperation.CREATE,
                  card_id: card_id,
                  mutationResultObject: mutationResult.data.createCampContact,
                });
              },
            });
            close();
          }}
        />
      </CustomDialog>
    );
  },
);

const EditItem = observer(
  ({ camp_id, headers, card_id }: { camp_id: number; headers: TableColumn[], card_id: number }) => {
    const stores = useStores();
    const modalStore = stores.modalStore;
    const [updateItem] = useUpdateCampContactMutation();

    const close = modalStore.closeUpdateItemModal;

    let existingItem = modalStore.existingItem;

    if (existingItem)
    {

      let permis = existingItem.permissions ? JSON.parse(existingItem.permissions) : [];

      for (const p of permis) {
        existingItem[`permission_${p.id}`] = p.value;
      }

    }



    return (
      <CustomDialog open={modalStore.showingUpdateItemModal} onClose={close}>
        <AutoTableModal
          title="kontakt"
          headers={headers}
          existingItem={existingItem}
          onCancel={close}
          onSubmit={(data) => {
            data.card_id = card_id;
            data.id = modalStore.existingItem.id;


            let permissionData = [];

            for (const [key, value] of Object.entries(data)) {
              if (key.includes('permission_')) {
                permissionData.push({
                  id: parseInt(key.split('_')[1]),
                  value: value
                });

                delete data[key];
              }
            }

            data.permissions = JSON.stringify(permissionData);

            updateItem({
              variables: {
                campContact: data as CampContactUpdate,
              },
            });
            close();
          }}
        />
      </CustomDialog>
    );
  },
);
